/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractGridInputCellEditorComponent } from './abstract-grid-input-cell-editor.component';

/** Line height used for popup height math (approximate visual rows). */
const TEXTAREA_LINE_HEIGHT_PX = 24;

/**
 * Long text often has few newline characters but wraps to many visual lines in the ~560px popup.
 * Without this, displayRows stays near minRows and the editor looks like a 3–5 line box.
 */
const ESTIMATED_CHARS_PER_LINE = 70;

const DEFAULT_MIN_ROWS = 4;
const DEFAULT_MAX_ROWS = 40;

@Component({
  standalone: false,
  selector: 'gh-grid-textarea-cell-editor',
  templateUrl: 'grid-textarea-cell-editor.component.html',
  styleUrls: ['grid-input-cell-editor.component.scss', 'grid-textarea-cell-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridTextareaCellEditorComponent extends AbstractGridInputCellEditorComponent implements OnDestroy {
  private valueChangesSub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {
    super();
  }

  get minRows(): number {
    return this.editorOptions?.minRows ?? DEFAULT_MIN_ROWS;
  }

  get maxRows(): number {
    return this.editorOptions?.maxRows ?? DEFAULT_MAX_ROWS;
  }

  /**
   * Logical rows for sizing: max of (explicit newlines) and (length-based wrap estimate).
   */
  get displayRows(): number {
    const minR = this.minRows;
    const maxR = this.maxRows;
    const text = this.control?.value == null ? '' : String(this.control.value);
    if (text === '') {
      return minR;
    }
    const newlineRows = text.split(/\r\n|\r|\n/).length;
    const lengthBasedRows = Math.max(1, Math.ceil(text.length / ESTIMATED_CHARS_PER_LINE));
    const neededRows = Math.max(newlineRows, lengthBasedRows);
    return Math.min(maxR, Math.max(minR, neededRows));
  }

  /**
   * Pixel height for the editor (and cap by viewport so popups stay on screen).
   * Textareas do not grow with content; we must set height explicitly — max-height alone keeps a small box.
   */
  get editorHeightPx(): number {
    const h = this.displayRows * TEXTAREA_LINE_HEIGHT_PX;
    if (typeof window === 'undefined') {
      return h;
    }
    return Math.min(h, Math.floor(window.innerHeight * 0.88));
  }

  isPopup(): boolean {
    return true;
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.valueChangesSub = this.control.valueChanges.subscribe(() => this.cdr.markForCheck());
  }

  override ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
    super.ngOnDestroy();
  }

  @HostListener('keydown', ['$event'])
  override onKeyDown(event: KeyboardEvent): void {
    super.onKeyDown(event);
    if (event.target instanceof HTMLTextAreaElement) {
      const { key, ctrlKey } = event;
      if (key === 'Enter' && !ctrlKey) {
        event.stopPropagation();
      }
      if (key === 'Enter' && ctrlKey) {
        event.preventDefault();
      }
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.stopPropagation();
      }
    }
  }
}
